import { useMutation } from '@tanstack/react-query'

import { useFindProfile } from '@hooks/profile'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { useFindBarriersQueries } from './queries'
import { IBarrier } from '../../interfaces/task/barrier'

interface IPutBarrierParams {
  changes: IBarrier
  barrierId: number
  taskId: number
}

export function useEditBarrier() {
  const { data: user } = useFindProfile()
  const { getBarrierCodesQuery, getBarriersQuery } = useFindBarriersQueries()

  const siteId = user && user.selectedSite.id

  async function putBarrier({ changes, barrierId, taskId }: IPutBarrierParams) {
    const url = `sites/${siteId}/tasks/${taskId}/barriers/${barrierId}`
    const { data } = await api.put<IBarrier>(url, changes)

    return data
  }

  return useMutation({
    mutationFn: putBarrier,
    onMutate: handleOptimisticEditBarrier,
    onError: (_err, _newBarrierComment, context) => {
      if (!context) return
      const { previousBarrier, barrierKey } = context
      queryClient.setQueryData(barrierKey, previousBarrier)
    },
    onSettled: (_barrierComments, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.barrierKey,
      }),
  })

  async function handleOptimisticEditBarrier({
    changes,
    barrierId,
    taskId,
  }: IPutBarrierParams) {
    const barrierQuery = await getBarriersQuery(taskId)
    const [barrierKey, barrierResult] = barrierQuery
    const barrierCode = await getBarrierCodesQuery(changes.barrierCodeId)

    const previousBarrier = barrierResult

    const updatedBarrierList = barrierResult?.map((barrier) => {
      if (barrier.id === barrierId) {
        return {
          ...barrier,
          barrierCode,
          hoursLost: changes.hoursLost,
          wasCausedByPreviousShift: changes.wasCausedByPreviousShift,
        }
      }
      return barrier
    })

    queryClient.setQueryData(barrierKey, () => updatedBarrierList)

    return { barrierKey, updatedBarrierList, previousBarrier }
  }
}
